import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Mail, CheckCircle2, AlertTriangle, Clock, Archive } from "lucide-react";

const STATUS_META = {
  sent: { label: "Sent", icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  queued: { label: "Queued", icon: Clock, cls: "bg-sky-50 text-sky-700 border-sky-200" },
  failed: { label: "Failed", icon: AlertTriangle, cls: "bg-rose-50 text-rose-700 border-rose-200" },
  config_missing: { label: "Config missing", icon: AlertTriangle, cls: "bg-amber-50 text-amber-700 border-amber-200" },
  outbox_only: { label: "Outbox only", icon: Archive, cls: "bg-slate-50 text-slate-700 border-slate-200" },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.outbox_only;
  const Icon = meta.icon;
  return (
    <span data-testid={`email-status-${status || "legacy"}`} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${meta.cls}`}>
      <Icon className="size-3" /> {meta.label}
    </span>
  );
}

export default function EmailOutbox() {
  const [emails, setEmails] = useState([]);
  useEffect(() => { api.get("/emails").then(r => setEmails(r.data)); }, []);
  return (
    <div className="p-4 md:p-8" data-testid="email-outbox-page">
      <div className="mb-6">
        <div className="overline mb-1">System</div>
        <h1 className="font-heading text-3xl font-black tracking-tighter sm:text-4xl">Email Outbox</h1>
        <p className="text-sm text-slate-600">All system-generated emails. When SMTP is enabled, the badge shows real delivery status; otherwise emails stay in the outbox for audit.</p>
      </div>
      <div className="space-y-3">
        {emails.map(em => (
          <Card key={em.id} className="border-slate-200 p-4" data-testid={`email-${em.id}`}>
            <div className="mb-1 flex items-center justify-between text-xs text-slate-500">
              <span><Mail className="inline mr-1 size-3"/> To: <b className="text-slate-900">{em.to}</b></span>
              <div className="flex items-center gap-2">
                <StatusBadge status={em.send_status} />
                <span className="font-mono">{new Date(em.sent_at).toLocaleString()}</span>
              </div>
            </div>
            <div className="font-heading text-base font-bold text-slate-900">{em.subject}</div>
            <pre className="mt-2 whitespace-pre-wrap font-sans text-xs text-slate-700">{em.body}</pre>
            {em.error_message && (
              <div className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700" data-testid={`email-error-${em.id}`}>
                <b>SMTP error:</b> {em.error_message}
              </div>
            )}
            {em.link && (
              <a href={em.link} target="_blank" rel="noreferrer" className="mt-2 inline-block rounded-md bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700" data-testid={`email-link-${em.id}`}>
                Open Sign-Off Link →
              </a>
            )}
          </Card>
        ))}
        {emails.length === 0 && <div className="text-slate-500">No emails yet.</div>}
      </div>
    </div>
  );
}
